/**
 * Public testimonials strip for the landing page. Renders only admin-published,
 * consented testimonials returned by GET /testimonials/public (anonymity already
 * enforced server-side: anonymous rows never carry a company name).
 */
import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";

export default function TestimonialsSection({ surface = "homepage", heading = "Operators who ran the scan", eyebrow = "What people say", bare = false }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/testimonials/public?surface=${encodeURIComponent(surface)}`);
        if (alive) setItems(data?.testimonials || []);
      } catch { /* silent — section just hides */ }
    })();
    return () => { alive = false; };
  }, [surface]);

  if (!items.length) return null;

  const grid = (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((t, i) => (
        <figure
          key={t.id || i}
          data-testid={`public-testimonial-${i}`}
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900 p-5 flex flex-col"
        >
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`h-4 w-4 ${n <= (t.rating || 0) ? "fill-amber-400 text-amber-500" : "text-slate-300 dark:text-slate-700"}`} />
            ))}
          </div>
          <blockquote className="mt-3 flex-1 text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
            &ldquo;{t.comment}&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            {t.name || "Anonymous"}
            {t.role ? <> · {t.role}</> : null}
            {t.name && t.organization ? <> · {t.organization}</> : null}
            {t.industry ? <> · {t.industry}</> : null}
          </figcaption>
        </figure>
      ))}
    </div>
  );

  if (bare) {
    return (
      <section data-testid="public-testimonials">
        {heading ? (
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight text-center">{heading}</h2>
        ) : null}
        {grid}
      </section>
    );
  }

  return (
    <section className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950" data-testid="public-testimonials">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">{eyebrow}</p>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          {heading}
        </h2>
        {grid}
      </div>
    </section>
  );
}
